import { useState } from 'react';
import { getServerNameCode } from '../../config/gcs/gcsConfigs';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';
import { en } from '../../locales/en';
import { useGcsStore } from '../../store/useGcsStore';
import type { MissionId } from '../../types/gcs/mission';
import type { VehicleId } from '../../types/gcs/vehicle';
import { AsyncAction } from '../../types/general/asyncAction';
import { getShortVehicleId } from '../../utils/formatters';
import { Text } from '../general/Text';
import Dialog from './Dialog';

interface ConfirmTakeoffDialogProps {
  gcsId: string;
  vehicleId: VehicleId;
  missionId: MissionId;
  onClose: () => void;
}

function ConfirmTakeoffDialog({ gcsId, vehicleId, missionId, onClose }: ConfirmTakeoffDialogProps) {
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const [takeoffAction, setTakeoffAction] = useState<AsyncAction>(AsyncAction.initial());
  const nameCode = getServerNameCode(gcsId);
  const vehicleLabel = getShortVehicleId(vehicleId);

  const launchpad = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    const launchpadId = gcsData?.vehicleLaunchpadMap.get(vehicleId);
    if (!launchpadId) return null;
    return gcsData?.launchpads.data?.find((l) => l.id === launchpadId);
  });

  const onTakeoffClicked = async () => {
    try {
      setTakeoffAction(AsyncAction.loading());
      await useGcsStore.getState().approveTakeoff(gcsId, missionId);
      setTakeoffAction(AsyncAction.success(undefined));
      onClose(); // Close dialog after successful takeoff
    } catch (error) {
      setTakeoffAction(AsyncAction.failure(error as Error));
    }
  };

  return (
    <Dialog
      title={en.confirmTakeoffDialog.title}
      onClose={onClose}
      primaryButtonProps={{
        text: en.confirmTakeoffDialog.confirmTakeoffButton,
        callback: onTakeoffClicked,
        isLoading: takeoffAction.isLoading,
        backgroundColor: 'var(--color-success)',
        foregroundColor: '#ffffff'
      }}
      errorText={takeoffAction.isFailure ? getLocalizedErrorMessage(takeoffAction.error) : undefined}
    >
      <div style={{ width: 'var(--dialog-common-width)' }}>
        <Text variant='body1'>
          {en.confirmTakeoffDialog.body
            .replace('{vehicle}', vehicleLabel)
            .replace('{launchpadLetter}', launchpad?.letter ?? en.common.notAvailable)
            .replace('{nameCode}', nameCode)}
        </Text>
      </div>
    </Dialog>
  );
}

export default ConfirmTakeoffDialog;
